import React, { useState, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import UUID from 'uuid/v4';
import { SocketContext } from '../../../../../../../api/socket';
import ConversationsList from '../../../../../Sidebar/ConversationsList/ConversationsList';
import DialogTitle from '../../../../../../shared/Dialog/DialogTitle/DialogTitle';
import { changeChat } from '../../../../../../../redux/actions/App';
import { makeStyles } from '@material-ui/styles';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Input from '@material-ui/core/Input';

const ForwardMessage = ({ handleClose, message }) => {
  const [selectedConversations, setSelectedConversations] = useState([]);
  const [isForwarding, setIsForwarding] = useState(false);
  const socket = useContext(SocketContext);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const toggleConversations = cnv => {
    setSelectedConversations(prevValue => {
      const conversation = prevValue.find(
        conversation => conversation.chat_id === cnv.chat_id
      );
      if (conversation) {
        const newSet = new Set(prevValue);
        newSet.delete(conversation);
        return [...newSet];
      }
      return [...prevValue, cnv];
    });
  };

  const handleSubmit = e => {
    e.preventDefault();
    submitMessage(message);
  };

  const submitMessage = async message => {
    setIsForwarding(true);
    await Promise.allSettled(
      selectedConversations.map(
        cnv =>
          new Promise((resolve, reject) => {
            const timeout = setTimeout(reject, 3000);
            socket.emit(
              'message',
              {
                chat_id: cnv.chat_id,
                body: message.body,
                forwards: message.message_id,
                message_short_id: UUID(),
              },
              () => {
                clearTimeout(timeout);
                resolve();
              }
            );
          })
      )
    );
    setIsForwarding(false);
    handleClose();
    if (selectedConversations.length === 1)
      return dispatch(changeChat(selectedConversations[0].chat_id));
  };

  const classes = makeStyles(theme => ({
    content: {
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: 0,
      paddingBottom: theme.spacing(1),
    },
    search: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(2),
      [theme.breakpoints.down('xs')]: {
        padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
      },
      '& span': { marginRight: theme.spacing(1) },
    },
    conversations: {
      marginTop: theme.spacing(2),
      maxHeight: 'calc(65 * var(--vh))',
      overflowY: 'auto',
      [theme.breakpoints.down('xs')]: { maxHeight: 'calc(50 * var(--vh))' },
    },
    subtitle: {
      marginBottom: theme.spacing(2.5),
      padding: `0 ${theme.spacing(2)}px`,
    },
    actions: { justifyContent: 'flex-start' },
  }))();

  return (
    <form onSubmit={handleSubmit}>
      <DialogTitle onClose={handleClose}>
        {t('MessageActions.Forward.title')}
      </DialogTitle>
      <Divider />
      <DialogContent className={classes.content}>
        <div className={classes.search}>
          <span>{t('MessageActions.Forward.to')}:</span>
          <Input
            fullWidth
            placeholder={t('MessageActions.Forward.search_conversations')}
            disableUnderline
          />
        </div>
        <Divider />

        <div className={classes.conversations}>
          <ConversationsList
            withSelect
            forward
            selectedConversations={selectedConversations.map(
              cnv => cnv.chat_id
            )}
            selectConversations={toggleConversations}
          />
        </div>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          data-testid="deleteButton"
          type="submit"
          disabled={!selectedConversations.length || isForwarding}
          color="primary"
          variant="contained"
          size="large"
        >
          {t('MessageActions.Forward.send')}
        </Button>
        <Button
          data-testid="cancelButton"
          onClick={handleClose}
          variant="outlined"
          color="primary"
          size="large"
        >
          {t('MessageActions.Forward.cancel')}
        </Button>
      </DialogActions>
    </form>
  );
};

export default ForwardMessage;
